// crm-frontend/src/components/SessionExpiredModal.tsx
// Modal shown when SESSION_REVOKED is detected (auth circuit breaker activated)
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiAlertCircle, FiLogIn, FiLogOut } from 'react-icons/fi';
import { resetAuthBlock } from '../api/apiClient';
import { createForceLogout } from '../utils/forceLogout';
import { queryClient } from '../lib/queryClient';
import { getCompanySlugFromLocation, resolveActiveCompanySlug, getRuntimeEnvFromHostname } from '../utils/companySlug';
import { isSuperadminPath, isPublicOrAuthPath } from '../utils/routeHelpers';

const SessionExpiredModal: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('הסשן פג תוקף. נא להתחבר מחדש');
  const navigate = useNavigate();
  const location = useLocation();
  const initialMountRef = React.useRef(true);
  const hasTokenRef = React.useRef(false);

  // Track if user has a token on mount (indicates they were logged in)
  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
    hasTokenRef.current = !!token;
    
    // Mark initial mount as complete after a short delay
    // This prevents the modal from showing if event fires during initial page load
    const timer = setTimeout(() => {
      initialMountRef.current = false;
    }, 2000); // 2 second grace period for initial page load

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Listen for session expired event from apiClient
    const handleSessionExpired = (event: CustomEvent) => {
      // CRITICAL: Don't show modal on initial page load
      // Only show if:
      // 1. Initial mount period has passed (2 seconds)
      // 2. User has/had a token (was logged in)
      // 3. Not on a login/auth route
      if (initialMountRef.current) {
        console.debug('[SessionExpiredModal] Ignoring session expired event during initial page load');
        return;
      }

      // Check if user has a token (was logged in)
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
      if (!token && !hasTokenRef.current) {
        console.debug('[SessionExpiredModal] Ignoring session expired event - no token found (user was never logged in)');
        return;
      }

      // Check if we're on a login/auth route - don't show modal there
      const pathname = location.pathname;
      if (isPublicOrAuthPath(pathname) || pathname.includes('/login') || pathname.includes('/register')) {
        console.debug('[SessionExpiredModal] Ignoring session expired event - on login/auth route');
        return;
      }

      const detail = event.detail || {};
      setMessage(detail.message || 'הסשן פג תוקף. נא להתחבר מחדש');
      setShowModal(true);
    };

    window.addEventListener('auth:session-expired' as any, handleSessionExpired as EventListener);

    return () => {
      window.removeEventListener('auth:session-expired' as any, handleSessionExpired as EventListener);
    };
  }, [location.pathname]);

  const handleLoginAgain = () => {
    // Reset auth block before navigating to login
    resetAuthBlock();
    
    // Get company slug using centralized helper (URL is source of truth)
    const pathname = location.pathname;
    const isSuperadminRoute = isSuperadminPath(pathname);
    
    // For superadmin routes, redirect to superadmin login
    if (isSuperadminRoute) {
      navigate('/superadmin/login');
      setShowModal(false);
      return;
    }
    
    // For company routes, try to extract company slug from URL first
    let companySlug = getCompanySlugFromLocation();
    
    // If not in URL (e.g., user is already on /home/login or another auth route),
    // try to get from localStorage (saved from previous session)
    if (!companySlug) {
      // Try localStorage v2 key first (most reliable)
      companySlug = localStorage.getItem('wjs_active_company_v2') || null;
      
      // Fallback to legacy key
      if (!companySlug) {
        companySlug = localStorage.getItem('currentCompany') || null;
      }
      
      // Try to get from client data if available
      if (!companySlug) {
        try {
          const clientStr = localStorage.getItem('client');
          if (clientStr) {
            const client = JSON.parse(clientStr);
            companySlug = client?.company?.slug || client?.company || null;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      // Validate company slug format
      if (companySlug && (
          companySlug === 'superadmin' || 
          companySlug === 'undefined' || 
          companySlug === 'null' ||
          companySlug.trim() === '' ||
          !/^[a-zA-Z0-9_-]+$/.test(companySlug)
        )) {
        companySlug = null;
      }
    }
    
    // Build login path with company slug
    // Use 'home' as fallback only if no company slug found
    const loginPath = companySlug ? `/${companySlug}/login` : '/home/login';
    
    // Navigate to login
    navigate(loginPath);
    setShowModal(false);
  };

  const handleLogout = () => {
    // Reset auth block
    resetAuthBlock();
    
    // Get company slug using centralized helper (URL is source of truth)
    const pathname = location.pathname;
    const isSuperadminRoute = isSuperadminPath(pathname);
    
    // For superadmin routes, no company slug needed
    if (isSuperadminRoute) {
      const forceLogout = createForceLogout(queryClient);
      forceLogout({
        reason: 'session-expired',
        source: 'SessionExpiredModal.handleLogout',
      });
      setShowModal(false);
      return;
    }
    
    // For company routes, extract company slug from URL or localStorage
    let companySlug: string | null = getCompanySlugFromLocation();
    
    // If not in URL, try to get from localStorage
    if (!companySlug) {
      const env = getRuntimeEnvFromHostname();
      const resolvedSlug = resolveActiveCompanySlug({ env });
      companySlug = resolvedSlug || null;
    }
    
    // Fallback: try to get from localStorage currentCompany key
    if (!companySlug) {
      companySlug = localStorage.getItem('currentCompany') || localStorage.getItem('wjs_active_company_v2');
    }
    
    // Try to get from client data if still not found
    if (!companySlug) {
      try {
        const clientStr = localStorage.getItem('client');
        if (clientStr) {
          const client = JSON.parse(clientStr);
          companySlug = client?.company?.slug || client?.company || null;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // Validate company slug format
    if (companySlug && (
        companySlug === 'superadmin' || 
        companySlug === 'undefined' || 
        companySlug === 'null' ||
        companySlug.trim() === '' ||
        !/^[a-zA-Z0-9_-]+$/.test(companySlug)
      )) {
      companySlug = null;
    }
    
    // Use force logout utility with company slug (convert null to undefined)
    const forceLogout = createForceLogout(queryClient);
    forceLogout({
      reason: 'session-expired',
      source: 'SessionExpiredModal.handleLogout',
      companySlug: companySlug || undefined,
    });
    
    setShowModal(false);
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-center mb-4">
          <FiAlertCircle className="text-red-500 text-4xl" />
        </div>
        <h2 className="text-xl font-bold text-center mb-4 text-gray-800">
          פג תוקף הסשן
        </h2>
        <p className="text-center text-gray-600 mb-6">
          {message}
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={handleLoginAgain}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiLogIn />
            התחבר מחדש
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-colors"
          >
            <FiLogOut />
            התנתק
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionExpiredModal;

